
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIndexedDB } from "react-indexed-db-hook";
import { useReactMediaRecorder } from "react-media-recorder";
import Dropzone, { IFileWithMeta, IStyleCustomization, StatusValue } from "react-dropzone-uploader";
import { authClient } from "../utils/amplifyClient";
import { Button as MUIButton } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { yellow } from "@mui/material/colors";
import {
    Badge as CSbadge,
    Box,
    Button,
    Checkbox,
    Container,
    Form,
    FormField,
    Grid,
    Header,
    Icon,
    Input,
    Link,
    ProgressBar,
    Select,
    Textarea,
} from './legacy-widgets';
import { InputMode } from "@aws-amplify/ui-react";
import { useNetworkState } from "@uidotdev/usehooks";
import * as Sentry from "@sentry/browser";
import { LoadingSpinner } from "./LoadingSpinner";
import { ReviewReport } from "./ReviewReport";
import { getForm as getFormQuery, listDiscrepancies as listDiscrepanciesQuery } from "../graphql/queries";

import { useUserContext } from "../contexts/UserContext";
import {
  DEFAULT_REPORT_LABELS,
  DEFAULT_FORM_INPUT_TYPES,
  INPUT_MODES,
  ITEM_STATUS_CODES,
  LOCAL_STORAGE_KEYS,
  LOCATION_TYPES,
  REPORT_TYPES,
  FORM_TYPES,
} from "../utils/constants";
import { appendToReportQueue, computeSummaryCounts, isReportUMCounterPromptLabel, resolveReportUMCounterValue, toAwsDateTime } from "../utils/reportUtils";
import { clearInspectionSession, loadInspectionDraft, saveInspectionDraft } from "../utils/inspectionDraftStorage";
import {
  buildInspectionMediaRecord,
  createInspectionMediaSessionId,
  matchesInspectionMediaScope,
  purgeStaleInspectionMedia,
  type InspectionMediaScope,
} from "../utils/inspectionMediaSession";
import { useOptionalNavigationGuard } from "../contexts/NavigationGuardContext";
import { mapSectionPrompt } from "../utils/formPromptMapping";
import { resolveActiveCompanyId } from "../utils/helperFunctions";
import { InspectionReportPayload, ShiftReportPayload } from "../types/reportTypes";
import { UMCounterLowReadingWarning } from "./UMCounterLowReadingWarning";

import { iInspectProps, tFormPrompts, tGPSInput, tGPSPosition, tInspectionFormInput, tMediaInput } from "../types";
import { TEIQspinner } from "./ui";
import { InspectFlowWizard } from "./inspection-flow/InspectFlowWizard";
import { InspectFlowReview } from "./inspection-flow/InspectFlowReview";
import { InspectionDiscardModal } from "./inspection-flow/InspectionDiscardModal";
import { hasInspectionProgress, shouldAutoAdvanceFourResponse, getFlowStepBlockedReason } from "./inspection-flow/inspectionWizardUtils";
import { useCompanyUnitsOfMeasure } from "../hooks/useCompanyUnitsOfMeasure";

import "react-dropzone-uploader/dist/styles.css";
import { Key } from "react-indexed-db-hook/lib/indexed-db";
import clsx from "clsx";
import { Stack } from "./ui";

const theme = createTheme({
  palette: {
    secondary: yellow,
  },
});

export const Inspect = ({
  backBtnAction,
  equipmentData,
  equipmentId,
  selectedUnitId,
  selectedUnit,
  uiVariant = 'legacy',
  equipmentTitle,
  formSubtitle,
  locationLabel = 'Unassigned',
}: iInspectProps) => {
  const network = useNetworkState();
  const { company, companyOwner, companyUser } = useUserContext();
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0); // --> Current step of the wizard
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isLocating, setIsLocating] = useState(false);
  const [userLocation, setUserLocation] = useState<tGPSInput | null>(null);
  const [alwaysTriggerEmail, setAlwaysTriggerEmail] = useState(false)
  const [formTypeValue, setFormTypeValue] = useState<string | null>(null)
  const [inspectionFormPrompts, setInspectionFormPrompts] = useState<tInspectionFormInput[]>([]);
  const [media, setMedia] = useState<{ [key: number]: tMediaInput[] } | null>(null);
  // const [mediaAction, setMediaAction] = useState<tMediaAction | null>(null);
  const [mediaQueue, setMediaQueue] = useState<{
    fileWithMeta: IFileWithMeta;
    status: StatusValue;
  }[]>([]);
  const [shouldReviewForm, setShouldReviewForm] = useState(false);
  const [audioReview, setAudioReview] = useState<{ [key: number]: Blob }>({});
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const discardExitModeRef = useRef<'hub' | 'pending' | null>(null);
  const mediaSessionIdRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const guard = useOptionalNavigationGuard();
  const { add, getByID, deleteRecord, getAll } = useIndexedDB("media");

  const getInspectionMediaScope = useCallback((): InspectionMediaScope | null => {
    if (!equipmentData?.formId || !selectedUnitId?.value) {
      return null;
    }

    if (!mediaSessionIdRef.current) {
      mediaSessionIdRef.current = createInspectionMediaSessionId();
    }

    return {
      formId: equipmentData.formId,
      equipmentCategoryId: equipmentId,
      unitId: selectedUnitId.value,
      mediaSessionId: mediaSessionIdRef.current,
    };
  }, [equipmentData?.formId, equipmentId, selectedUnitId?.value]);

  const companyId = resolveActiveCompanyId({ companyUser, companyOwner, company });
  const { catalogOptions: umCatalog } = useCompanyUnitsOfMeasure(
    companyId ?? '',
    selectedUnit?.unitOfMeasure ? [String(selectedUnit.unitOfMeasure)] : [],
  );

  const getActiveDiscrepancies = async () => {
    try {
      // First get all active discrepancies for this equipment/unit
      const discrepanciesResponse = await authClient.graphql({
        query: listDiscrepanciesQuery,
        variables: {
          filter: {
            equipmentId: { eq: equipmentId },
            ...(selectedUnitId?.value ? { unitId: { eq: selectedUnitId.value } } : {}),
          }
        }
      });

      return discrepanciesResponse.data.listDiscrepancies.items.filter((issue) => issue.reportId)
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
      return []
    }
  }

  useEffect(() => {

    if (equipmentData?.id && !inspectionFormPrompts.length && selectedUnitId?.value) {
      const draft = loadInspectionDraft(equipmentId, selectedUnitId.value);
      if (draft) {
        mediaSessionIdRef.current = draft.mediaSessionId ?? createInspectionMediaSessionId();
        setInspectionFormPrompts(draft.prompts);
        setActiveStepIndex(draft.activeStepIndex);
        setShouldReviewForm(draft.shouldReviewForm);
        setIsPageLoading(false);
        return;
      }

      mediaSessionIdRef.current = createInspectionMediaSessionId();
      beginInspection()
    }
  }, [equipmentData.id, network.online, selectedUnitId?.value]);

  useEffect(() => {
    const mediaScope = getInspectionMediaScope();
    if (!mediaScope || !inspectionFormPrompts.length) {
      return;
    }

    let isCancelled = false;

    (async () => {
      try {
        const allRecords = await getAll();
        if (isCancelled || !allRecords?.length) {
          return;
        }

        const formRecords = allRecords.filter(
          (record) =>
            matchesInspectionMediaScope(record, mediaScope) &&
            typeof record?.question === 'number',
        );
        if (!formRecords.length) {
          return;
        }

        const byQuestion: { [key: number]: tMediaInput[] } = {};
        for (const record of formRecords) {
          const question = record.question as number;
          const item: tMediaInput = {
            id: record.id,
            name: record.name,
            data: record,
          };
          if (!byQuestion[question]) {
            byQuestion[question] = [];
          }
          if (!byQuestion[question].some((existing) => existing.id === item.id)) {
            byQuestion[question].push(item);
          }
        }

        setMedia((prev) => {
          const next = { ...(prev ?? {}) };
          for (const [questionKey, items] of Object.entries(byQuestion)) {
            const step = Number(questionKey);
            const existing = next[step] ?? [];
            const seen = new Set(existing.map((item) => item.id));
            const merged = [...existing];
            for (const item of items) {
              if (item.id != null && !seen.has(item.id)) {
                seen.add(item.id);
                merged.push(item);
              }
            }
            if (merged.length) {
              next[step] = merged;
            }
          }
          return Object.keys(next).length ? next : prev;
        });
      } catch (error) {
        console.error('Failed to rehydrate inspection media', error);
        Sentry.captureException(error);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [equipmentData?.formId, equipmentId, inspectionFormPrompts.length, getAll, selectedUnitId?.value, getInspectionMediaScope]);

  // TODO: make sure the back/next buttons are inside the container instead of below it -- hard to view with darker background

  const beginInspection = async () => {
    setIsLoading(true);
    try {
      const mediaScope = getInspectionMediaScope();
      if (mediaScope) {
        await purgeStaleInspectionMedia(getAll, deleteRecord, mediaScope);
      }

      const formPrompts = await getForm();
      const activeDiscrepancies = await getActiveDiscrepancies();

      const relevantDiscrepancies = activeDiscrepancies.filter(
        disc => disc.status !== ITEM_STATUS_CODES.ok
      );

      const processedForm = formPrompts.map((prompt: tInspectionFormInput) => {
        const promptKey = `${prompt.title}-${prompt.label}-${prompt.description}`;
        const matchingDiscrepancy = relevantDiscrepancies.find(disc =>
          `${disc.promptTitle}-${disc.promptLabel}-${disc.promptDescription}` === promptKey
        );

        let discrepancyNotes = '';
        let discrepancyStatus = null;

        if (matchingDiscrepancy) {
          // Preserve the actual status from the discrepancy
          discrepancyStatus = matchingDiscrepancy.status

          if (matchingDiscrepancy.log) {
            try {
              const logEntries = JSON.parse(matchingDiscrepancy.log);
              const relevantEntry = logEntries.find((entry: { notes: string | string[]; }) =>
                entry && entry.notes && !entry.notes.includes('[Updated Status:')
              );
              discrepancyNotes = relevantEntry?.notes || '';
            } catch (e) {
              console.error('Error parsing discrepancy log:', e);
              discrepancyNotes = '';
            }
          }
        }

        return {
          ...prompt,
          hasActiveDiscrepancy: !!matchingDiscrepancy,
          discrepancyNotes,
          discrepancyStatus,
          value: discrepancyStatus || '' // Set initial value to the discrepancy status if it exists
        };
      });
      setInspectionFormPrompts(processedForm);

    } catch (error) {
      console.error('Error processing inspection:', error);
      Sentry.captureException(error);
    } finally {
      setIsLoading(false);
      setIsPageLoading(false);
    }
  };

  const getForm = async () => {
    const formId = equipmentData.formId;
    if (!formId) {
      throw new Error('This equipment category has no inspection form assigned.');
    }

    try {
      const formResponse =
        network.online && companyId
          ? await authClient.graphql({
            query: getFormQuery,
            variables: { id: formId },
          })
          : JSON.parse(localStorage.getItem(`form_${formId}`) || '{}');

      setAlwaysTriggerEmail(formResponse.data.getForm.alwaysTriggerEmail)
      setFormTypeValue(formResponse.data.getForm.formType || null)

      const prompts: tInspectionFormInput[] = formResponse?.data?.getForm?.noMachineHours
        ? []
        : [
          {
            type: DEFAULT_FORM_INPUT_TYPES.standAloneQuestion,
            label: DEFAULT_REPORT_LABELS.currentMachineHours,
            inputMode: INPUT_MODES.NUMBER,
            description: "",
            value: "",
            notes: "",
            photo: [],
            title: "",
            hasActiveDiscrepancy: false,
            discrepancyNotes: "",
          },
        ];

      if (formResponse?.data?.getForm?.formData) {
        JSON.parse(formResponse.data.getForm.formData).forEach(
          (formData: tFormPrompts) => {
            if (formData.type === DEFAULT_FORM_INPUT_TYPES.standAloneQuestion) {
              prompts.push({
                type: DEFAULT_FORM_INPUT_TYPES.standAloneQuestion,
                label: formData.prompt,
                inputMode: formData.inputMode?.value || "text",
                description: "",
                value: "",
                notes: "",
                photo: [],
                title: "",
                hasActiveDiscrepancy: false,
                discrepancyNotes: "",
              });
            } else {
              formData.prompts?.length && formData.prompts.forEach((prompt) => {
                prompts.push(mapSectionPrompt(formData.name || '', prompt));
              });
            }
          }
        );
      }

      return prompts; // Only return the prompts, don't set state here

    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
      return [];
    } finally {
      if (uiVariant !== 'flow') {
        setIsPageLoading(false);
      }
    }
  };

  const flashStyle = `
 .flash-warning {
    animation: flashBorder 4s ease 0s infinite normal forwards;
  }

  .flash-warning.warning-status {
    --flash-color: #ffc107;    border: 2px solid var(--flash-color);
  }

  .flash-warning:not(.warning-status) {
    --flash-color: #113ec4;
    border: 2px solid var(--flash-color);
  }

  @keyframes flashBorder {
    0%, 50%, 100% {
      border-color: var(--flash-color);
      box-shadow: 0 0 10px 4px var(--flash-color);
    }
    25%, 75% {
      border-color: var(--flash-color);
      box-shadow: 0 0 1px 0px var(--flash-color);
    }
  }
`;
  // Add the style to your component
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = flashStyle;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const getMimeType = () => {
    const types = [
      'audio/webm',
      'audio/mp4',
      'audio/ogg',
      'audio/wav'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    // If none of the preferred MIME types are supported, 
    // let the browser use its default
    return '';
  };

  const { clearBlobUrl, status, startRecording, stopRecording } =
    useReactMediaRecorder({
      mediaRecorderOptions: {
        mimeType: getMimeType(),
        //switched from audio/wav to fix MIME errors - if this causes a problem can go back
      },
      audio: true,
      onStop: async (_url, blob) => {
        const mediaScope = getInspectionMediaScope();
        if (!mediaScope) {
          return;
        }

        const obj = buildInspectionMediaRecord(
          blob,
          "audio.wav",
          activeStepIndex,
          mediaScope,
        );
        const event = await add(obj);
        setAudioReview({
          ...audioReview,
          [activeStepIndex]: blob,
        });
        setMedia((prev) => {
          return {
            ...prev,
            [activeStepIndex]: (prev && prev[activeStepIndex])
              ? [
                ...prev[activeStepIndex],
                {
                  id: event,
                  name: "audio.wav",
                },
              ]
              : [
                {
                  id: event,
                  name: "audio.wav",
                },
              ],
          };
        });
      },
    });

  const coordinates =
    JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.LOCATION) || '{}') || {};

  const createInspectionReport = () => {
    if (!companyId) {
      return;
    }

    setIsLoading(true);

    const machineHoursValue = resolveReportUMCounterValue(inspectionFormPrompts);

    const { okCount, warnCount, inopCount, totalItems } = computeSummaryCounts(inspectionFormPrompts);

    // Route based on formType — shift report forms go to ShiftReport table
    if (formTypeValue === FORM_TYPES.SHIFT_REPORT) {
      // Determine shiftType from equipment/form name
      const nameLower = (equipmentData.name || '').toLowerCase();
      const shiftType: 'start-of-day' | 'end-of-day' = nameLower.includes('end') ? 'end-of-day' : 'start-of-day';

      // Extract locationName from first dropdown prompt with 'location' in label
      const locationPrompt = inspectionFormPrompts.find(
        (p) => p.subType === 'dropdown' && p.label?.toLowerCase().includes('location')
      );
      const locationName = locationPrompt?.value || selectedUnitId?.label || undefined;

      const payload: ShiftReportPayload = {
        reportType: REPORT_TYPES.SHIFT as 'ShiftReport',
        shiftType,
        companyId,
        userId: '',
        userName: selectedUnitId?.label || '',
        formId: equipmentData.formId ?? '',
        locationId: selectedUnit?.locationId ?? undefined,
        locationName,
        alwaysTriggerEmail,
        gpsLatitude: userLocation?.latitude,
        gpsLongitude: userLocation?.longitude,
        gpsLocationType: userLocation?.type,
        inspectionStarted: toAwsDateTime(localStorage.getItem(LOCAL_STORAGE_KEYS.TIME_INSPECTION_STARTED)),
        okCount,
        warnCount,
        inopCount,
        totalItems,
        responseData: JSON.stringify(inspectionFormPrompts),
        media: JSON.stringify(media),
      };

      appendToReportQueue(payload);
    } else {
      // Default: InspectionReport
      const payload: InspectionReportPayload = {
        reportType: REPORT_TYPES.INSPECTION as 'InspectionReport',
        companyId: companyId,
        equipmentCategoryId: equipmentId,
        unitId:
          selectedUnitId?.value ||
          localStorage.getItem(LOCAL_STORAGE_KEYS.UNIT_ID) ||
          undefined,
        userId: '',
        formId: equipmentData.formId ?? '',
        equipmentCategoryName: equipmentData.name,
        unitName: selectedUnitId?.label,
        userName: '',
        locationId: selectedUnit?.locationId ?? '',
        machineHoursAtInspection: machineHoursValue,
        umCounterAtInspection: machineHoursValue,
        gpsLatitude: userLocation?.latitude,
        gpsLongitude: userLocation?.longitude,
        gpsLocationType: userLocation?.type,
        alwaysTriggerEmail: alwaysTriggerEmail,
        inspectionStarted: toAwsDateTime(localStorage.getItem(LOCAL_STORAGE_KEYS.TIME_INSPECTION_STARTED)),
        okCount,
        warnCount,
        inopCount,
        totalItems,
        responseData: JSON.stringify(inspectionFormPrompts),
        media: JSON.stringify(media),
      };

      appendToReportQueue(payload);
    }

    localStorage.setItem(LOCAL_STORAGE_KEYS.EQUIPMENT_NAME, equipmentData.name);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_REPORT)
    clearInspectionSession();
    setIsLoading(false);
    window.history.pushState(null, '', "/");
    navigate("/report-sent", { replace: true });
  };

  useEffect(() => {
    if (!equipmentId || !selectedUnitId?.value || !inspectionFormPrompts.length) {
      return;
    }

    saveInspectionDraft({
      equipmentCategoryId: equipmentId,
      unitId: selectedUnitId.value,
      mediaSessionId: mediaSessionIdRef.current ?? undefined,
      activeStepIndex,
      shouldReviewForm,
      prompts: inspectionFormPrompts,
      updatedAt: new Date().toISOString(),
    });
  }, [activeStepIndex, equipmentId, inspectionFormPrompts, selectedUnitId?.value, shouldReviewForm]);

  useEffect(() => {
    setLocationFunction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.INSPECT, equipmentId);
    localStorage.setItem(LOCAL_STORAGE_KEYS.EQUIPMENT_NAME, selectedUnitId?.label ? `${equipmentData.name} - ${selectedUnitId.label}` : equipmentData.name)
    localStorage.setItem(LOCAL_STORAGE_KEYS.UNIT_ID, selectedUnitId ? selectedUnitId?.value : "")
    localStorage.setItem(LOCAL_STORAGE_KEYS.SELECTED_UNIT, selectedUnit ? JSON.stringify(selectedUnit) : '')
  }, [equipmentId]);

  useEffect(() => {
    const storePosition = (position: tGPSPosition) => {
      // hasPosition = true;
      setUserLocation({
        type: LOCATION_TYPES.gps,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.LOCATION,
        JSON.stringify({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        })
      );
      setIsLocating(false);
    };

    if (isLocating) {
      navigator.geolocation.getCurrentPosition(
        storePosition,
        () => {
          setUserLocation({
            type: coordinates?.latitude
              ? LOCATION_TYPES.lastKnownPosition
              : LOCATION_TYPES.unknown,
            ...coordinates,
          });
          setIsLocating(false);
        },
        { timeout: 10000 }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocating]);

  useEffect(() => {
    const processMediaQueue = async () => {
      if (mediaQueue.length === 0) return;

      const [currentMedia, ...remainingQueue] = mediaQueue;

      if (currentMedia.status === "removed") {
        if (!media) return;

        const recordToDelete = media[activeStepIndex]?.find(
          (el) => el.name === currentMedia.fileWithMeta.file.name
        )?.id;

        if (recordToDelete) {
          await deleteRecord(recordToDelete);
          setMedia((prev) => ({
            ...prev,
            [activeStepIndex]: media[activeStepIndex].filter(
              (el) => el.name !== currentMedia.fileWithMeta.file.name
            ),
          }));
        }
      } else if (currentMedia.status === "done") {
        try {
          const mediaScope = getInspectionMediaScope();
          if (!mediaScope) {
            return;
          }

          const obj = buildInspectionMediaRecord(
            currentMedia.fileWithMeta.file,
            currentMedia.fileWithMeta.file.name,
            activeStepIndex,
            mediaScope,
          );
          const event = await add(obj);
          const photoData = await getByID(event);

          setMedia((prev) => ({
            ...prev,
            [activeStepIndex]: (prev && prev[activeStepIndex])
              ? [
                ...prev[activeStepIndex],
                {
                  id: event,
                  name: currentMedia.fileWithMeta.file.name,
                  data: photoData,
                },
              ]
              : [
                {
                  id: event,
                  name: currentMedia.fileWithMeta.file.name,
                  data: photoData,
                },
              ],
          }));
        } catch (error) {
          console.log(error);
          Sentry.captureException(error);
        }
      }

      setMediaQueue(remainingQueue);
    };

    processMediaQueue();
  }, [mediaQueue, activeStepIndex, media, equipmentData.formId, add, getByID, deleteRecord, getInspectionMediaScope]);

  const setLocationFunction = async () => {
    if (navigator.geolocation) {
      setIsLocating(true);
    } else {
      if (coordinates?.latitude) {
        setUserLocation({
          type: LOCATION_TYPES.lastKnownPosition,
          ...coordinates,
        });
      }
    }
  };

  useEffect(() => {
    if (!inspectionFormPrompts[activeStepIndex]) return;

    const currentPrompt = inspectionFormPrompts[activeStepIndex];

    // Only update if:
    // 1. The prompt has an active discrepancy
    // 2. No new notes have been added
    // 3. There are discrepancy notes to use
    // 4. Notes haven't been modified yet
    if (
      currentPrompt.hasActiveDiscrepancy &&
      !currentPrompt.notes &&
      currentPrompt.discrepancyNotes &&
      !currentPrompt.notesModified
    ) {
      const updatePrompts = [...inspectionFormPrompts];
      updatePrompts[activeStepIndex] = {
        ...currentPrompt,
        notes: currentPrompt.discrepancyNotes,
        notesModified: false // Track that these are original discrepancy notes
      };
      setInspectionFormPrompts(updatePrompts);
    }
  }, [activeStepIndex, inspectionFormPrompts]);

  const handleChangeStatus = (fileWithMeta: IFileWithMeta, dropZoneStatus: StatusValue) => {
    if (dropZoneStatus === "removed" || dropZoneStatus === "done") {
      setMediaQueue(prev => [...prev, {
        fileWithMeta,
        status: dropZoneStatus
      }]);
    }
  };
  const isAudioRecordingStopped = (stateOfRecorder: string) => {
    return stateOfRecorder === "stopped" || stateOfRecorder === "idle";
  };

  const getLocationStatusColor = () => {
    switch (userLocation?.type) {
      case LOCATION_TYPES.gps:
        return "green";
      case LOCATION_TYPES.lastKnownPosition:
        return "blue";
      case null:
      default:
        return "grey";
    }
  };

  const currentPrompt = inspectionFormPrompts[activeStepIndex];
  const useFlowWizard =
    uiVariant === 'flow' &&
    !shouldReviewForm &&
    inspectionFormPrompts.length > 0 &&
    Boolean(currentPrompt);

  const inspectionHasProgress = hasInspectionProgress(
    inspectionFormPrompts,
    activeStepIndex,
    shouldReviewForm,
    media,
    audioReview,
  );

  const clearInspectionSessionMedia = async () => {
    if (!media) {
      return;
    }

    const mediaIds = Object.values(media)
      .flat()
      .map((item) => item.id)
      .filter((id): id is number => id != null);

    for (const id of mediaIds) {
      try {
        await deleteRecord(id);
      } catch (error) {
        console.error('Failed to delete inspection media record', error);
        Sentry.captureException(error);
      }
    }
  };

  const resetInspectionWizardState = () => {
    clearBlobUrl();
    setMedia(null);
    setAudioReview({});
    setMediaQueue([]);
    mediaSessionIdRef.current = null;
    setActiveStepIndex(0);
    setShouldReviewForm(false);
    setInspectionFormPrompts([]);
  };

  const discardInspectionSession = async () => {
    await clearInspectionSessionMedia();
    clearInspectionSession();
    resetInspectionWizardState();
    guard?.registerGuard(null);
  };

  const restartInspection = async () => {
    await discardInspectionSession();
    guard?.cancelPendingNavigation();
    setShowDiscardModal(false);
    discardExitModeRef.current = null;
    await beginInspection();
  };

  const exitInspectionToMenu = async () => {
    const exitMode = discardExitModeRef.current;
    await discardInspectionSession();
    setShowDiscardModal(false);
    discardExitModeRef.current = null;

    if (exitMode === 'pending') {
      guard?.confirmPendingNavigation();
      return;
    }

    backBtnAction();
  };

  const requestExitInspection = () => {
    if (!inspectionHasProgress) {
      void exitInspectionToMenu();
      return;
    }

    discardExitModeRef.current = 'hub';
    setShowDiscardModal(true);
  };

  const requestStartOver = () => {
    if (!inspectionHasProgress) {
      return;
    }

    discardExitModeRef.current = null;
    setShowDiscardModal(true);
  };

  useEffect(() => {
    if (!guard || uiVariant !== 'flow' || !inspectionFormPrompts.length) {
      return;
    }

    guard.registerGuard(
      inspectionHasProgress
        ? {
            when: true,
            onBlocked: () => {
              discardExitModeRef.current = 'pending';
              setShowDiscardModal(true);
            },
          }
        : null,
    );

    return () => guard.registerGuard(null);
  }, [guard, inspectionHasProgress, inspectionFormPrompts.length, uiVariant]);

  const getFlowStepMediaItems = (): tMediaInput[] => {
    const prompt = inspectionFormPrompts[activeStepIndex];
    const fromMedia = media?.[activeStepIndex] ?? [];
    const fromPrompt = prompt?.photo ?? [];
    const byId = new Map<number, tMediaInput>();

    for (const item of [...fromPrompt, ...fromMedia]) {
      if (item?.id == null) {
        continue;
      }

      const existing = byId.get(item.id);
      if (!existing || (!existing.data?.file && item.data?.file)) {
        byId.set(item.id, item);
      }
    }

    return Array.from(byId.values());
  };

  const canGoNextStep = (() => {
    const prompt = inspectionFormPrompts[activeStepIndex];
    if (!prompt) {
      return false;
    }

    const requiresValue =
      prompt.subType !== DEFAULT_FORM_INPUT_TYPES.checkbox &&
      prompt.subType !== DEFAULT_FORM_INPUT_TYPES.dropdown;

    if (requiresValue && !prompt.value) {
      return false;
    }

    const stepMediaItems = getFlowStepMediaItems();
    const hasWarnInopEvidence =
      (prompt.notes && prompt.notes.length > 0) ||
      stepMediaItems.some((item) => item.data?.file);

    if (
      (prompt.value === ITEM_STATUS_CODES.warn || prompt.value === ITEM_STATUS_CODES.inop) &&
      !hasWarnInopEvidence
    ) {
      return false;
    }

    return true;
  })();

  const nextStepBlockedReason =
    !canGoNextStep && !isLoading
      ? getFlowStepBlockedReason(currentPrompt, getFlowStepMediaItems())
      : null;

  const attachMediaToCurrentPrompt = () => {
    if (media && media[activeStepIndex]) {
      media[activeStepIndex].forEach((mediaItem) => {
        if (!inspectionFormPrompts[activeStepIndex]?.photo?.includes(mediaItem)) {
          inspectionFormPrompts[activeStepIndex]?.photo?.push(mediaItem);
        }
      });
    }
  };

  const advanceFlowStep = () => {
    clearBlobUrl();
    attachMediaToCurrentPrompt();

    if (activeStepIndex < inspectionFormPrompts.length - 1) {
      setActiveStepIndex(activeStepIndex + 1);
      return;
    }

    setShouldReviewForm(true);
  };

  const handleFlowPrevious = () => {
    clearBlobUrl();
    setActiveStepIndex((index) => Math.max(0, index - 1));
  };

  const handleFlowNext = () => {
    advanceFlowStep();
  };

  const handlePromptValueChange = (index: number, value: string) => {
    const updatePrompts = [...inspectionFormPrompts];
    updatePrompts[index] = { ...updatePrompts[index], value };
    setInspectionFormPrompts(updatePrompts);
  };

  const handleFlowPromptValueChange = (index: number, value: string) => {
    handlePromptValueChange(index, value);

    if (index !== activeStepIndex || !shouldAutoAdvanceFourResponse(value)) {
      return;
    }

    advanceFlowStep();
  };

  const handlePromptNotesChange = (index: number, notes: string) => {
    const updatePrompts = [...inspectionFormPrompts];
    updatePrompts[index] = { ...updatePrompts[index], notes, notesModified: true };
    setInspectionFormPrompts(updatePrompts);
  };

  const handleFlowPromptCheckboxToggle = (index: number, option: string) => {
    const updatePrompts = [...inspectionFormPrompts];
    const currentSelected = updatePrompts[index]?.selectedOptions || [];

    if (currentSelected.includes(option)) {
      updatePrompts[index] = {
        ...updatePrompts[index],
        selectedOptions: currentSelected.filter((val: string) => val !== option),
      };
    } else {
      updatePrompts[index] = {
        ...updatePrompts[index],
        selectedOptions: [...currentSelected, option],
      };
    }

    setInspectionFormPrompts(updatePrompts);
  };

  const handleFlowAddFiles = async (files: FileList) => {
    const mediaScope = getInspectionMediaScope();
    if (!mediaScope) {
      return;
    }

    for (const file of Array.from(files)) {
      try {
        const obj = buildInspectionMediaRecord(
          file,
          file.name,
          activeStepIndex,
          mediaScope,
        );
        const id = await add(obj);
        const photoData = await getByID(id);
        const mediaItem: tMediaInput = {
          id,
          name: file.name,
          data: photoData,
        };

        setMedia((prev) => ({
          ...prev,
          [activeStepIndex]: [...(prev?.[activeStepIndex] ?? []), mediaItem],
        }));

        setInspectionFormPrompts((prev) => {
          const updatePrompts = [...prev];
          const prompt = updatePrompts[activeStepIndex];
          if (!prompt) {
            return prev;
          }

          const photo = prompt.photo ?? [];
          if (photo.some((item) => item.id === id)) {
            return prev;
          }

          updatePrompts[activeStepIndex] = {
            ...prompt,
            photo: [...photo, mediaItem],
          };
          return updatePrompts;
        });
      } catch (error) {
        console.error('Failed to save inspection photo', error);
        Sentry.captureException(error);
      }
    }
  };

  const handleFlowRemoveMediaItem = async (item: tMediaInput) => {
    if (item.id) {
      await deleteRecord(item.id);
    }

    setMedia((prev) => ({
      ...prev,
      [activeStepIndex]: (prev?.[activeStepIndex] ?? []).filter((el) => el.id !== item.id),
    }));

    const updatePrompts = [...inspectionFormPrompts];
    if (updatePrompts[activeStepIndex]?.photo?.length) {
      updatePrompts[activeStepIndex] = {
        ...updatePrompts[activeStepIndex],
        photo: updatePrompts[activeStepIndex].photo?.filter((el) => el.id !== item.id),
      };
      setInspectionFormPrompts(updatePrompts);
    }
  };

  const handleFlowRemoveAudio = async () => {
    clearBlobUrl();

    const audioItems = media?.[activeStepIndex]?.filter((el) => el.name === 'audio.wav') ?? [];
    for (const item of audioItems) {
      if (item.id) {
        await deleteRecord(item.id);
      }
    }

    setMedia((prev) => ({
      ...prev,
      [activeStepIndex]: (prev?.[activeStepIndex] ?? []).filter((el) => el.name !== 'audio.wav'),
    }));

    setAudioReview((prev) => {
      const next = { ...prev };
      delete next[activeStepIndex];
      return next;
    });
  };

  if (uiVariant === 'flow' && isPageLoading) {
    return (
      <div className="page-container inspection-flow-wizard-page" aria-busy="true">
        <TEIQspinner variant="lg" label="Loading inspection" />
      </div>
    );
  }

  if (uiVariant === 'flow' && shouldReviewForm && inspectionFormPrompts.length > 0) {
    return (
          <div className="page-container inspection-flow-wizard-page">
            <InspectFlowReview
              equipmentTitle={equipmentTitle || selectedUnit?.name || equipmentData?.name || 'Inspection'}
              formSubtitle={formSubtitle || 'Inspection Form'}
              prompts={inspectionFormPrompts}
              isSubmitting={isLoading}
              onExit={requestExitInspection}
              onGoBack={() => setShouldReviewForm(false)}
              onSubmit={createInspectionReport}
            />
            <InspectionDiscardModal
              visible={showDiscardModal}
              onDismiss={() => {
                guard?.cancelPendingNavigation();
                discardExitModeRef.current = null;
                setShowDiscardModal(false);
              }}
              onStartOver={() => {
                void restartInspection();
              }}
              onExit={() => {
                void exitInspectionToMenu();
              }}
            />
          </div>
    );
  }

  if (useFlowWizard) {
    return (
          <div className="page-container inspection-flow-wizard-page">
            <InspectFlowWizard
              equipmentTitle={equipmentTitle || selectedUnit?.name || equipmentData?.name || 'Inspection'}
              formSubtitle={formSubtitle || 'Inspection Form'}
              locationLabel={locationLabel}
              prompts={inspectionFormPrompts}
              activeStepIndex={activeStepIndex}
              userLocation={userLocation}
              selectedUnit={selectedUnit}
              umCatalog={umCatalog}
              isLoading={isLoading}
              canGoNext={canGoNextStep}
              nextStepBlockedReason={nextStepBlockedReason}
              showStartOver={inspectionHasProgress}
              onExit={requestExitInspection}
              onStartOver={requestStartOver}
              onPrevious={handleFlowPrevious}
              onNext={handleFlowNext}
              onPromptValueChange={handleFlowPromptValueChange}
              onPromptNotesChange={handlePromptNotesChange}
              onPromptCheckboxToggle={handleFlowPromptCheckboxToggle}
              stepMediaItems={getFlowStepMediaItems()}
              audioBlob={audioReview[activeStepIndex]}
              recorderStatus={status}
              onFilesSelected={handleFlowAddFiles}
              onStartRecording={startRecording}
              onStopRecording={stopRecording}
              onRemoveAudio={handleFlowRemoveAudio}
              onRemoveMediaItem={handleFlowRemoveMediaItem}
            />
            <InspectionDiscardModal
              visible={showDiscardModal}
              onDismiss={() => {
                guard?.cancelPendingNavigation();
                discardExitModeRef.current = null;
                setShowDiscardModal(false);
              }}
              onStartOver={() => {
                void restartInspection();
              }}
              onExit={() => {
                void exitInspectionToMenu();
              }}
            />
          </div>
    );
  }

  if (uiVariant === 'flow') {
    return (
      <div className="page-container inspection-flow-wizard-page" aria-busy="true">
        <TEIQspinner variant="lg" label="Loading inspection" />
      </div>
    );
  }

  return (
    <LoadingSpinner
      isLoading={isPageLoading}
      content={
        <ThemeProvider theme={theme}>
          <Stack size="l">
            <Grid
              gridDefinition={[
                { colspan: { default: 12 } },
              ]}
            >
              {equipmentData?.name && (
                <Header
                  description={
                    <CSbadge color={`${getLocationStatusColor()}`}>
                      Location:{" "}
                      {userLocation?.type || "Getting coordinates from device..."}
                    </CSbadge>
                  }
                  variant="h1"
                  actions={<Link onFollow={() => backBtnAction()}>Back</Link>}
                >{`Inspection of: ${equipmentData.name}${selectedUnitId ? ` - ${selectedUnitId.label}` : ""
                  }`}</Header>
              )}
              {/* <Flex justifyContent="center">
              {equipmentData?.image ? (
                <img
                  className="equipment-image"
                  src={equipmentData?.image}
                  alt={equipmentData?.name}
                />
              ) : (
                <></>
              )}
            </Flex> */}
            </Grid>
            {shouldReviewForm && (
              <ReviewReport
                cancel={() => setShouldReviewForm(false)}
                inspectionFormPrompts={inspectionFormPrompts}
                media={media}
                submit={createInspectionReport}
              />
            )}
            {!shouldReviewForm && (
              <>
                <Container>
                  <Stack size="l">
                    <Box color="text-status-success" margin={{ top: "l" }}>
                      Progress
                      <ProgressBar
                        value={
                          (activeStepIndex / inspectionFormPrompts.length) * 100 || 0
                        }
                      // label="Progress"
                      />
                    </Box>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                      }}
                    >
                      <Form
                        actions={
                          <Stack direction="horizontal" size="m">
                            {activeStepIndex > 0 && (
                              <Button
                                variant="normal"
                                onClick={() => {
                                  clearBlobUrl();
                                  setActiveStepIndex(activeStepIndex - 1);
                                }}
                              >
                                Previous
                              </Button>
                            )}
                            <Button
                              variant="primary"
                              disabled={ // Next button should be disabled if... 
                                inspectionFormPrompts[activeStepIndex]?.subType !== DEFAULT_FORM_INPUT_TYPES.checkbox &&
                                inspectionFormPrompts[activeStepIndex]?.subType !== DEFAULT_FORM_INPUT_TYPES.dropdown &&
                                !inspectionFormPrompts[activeStepIndex]?.value || // There is no value stored (e.g. "ok") OR ...
                                ((inspectionFormPrompts[activeStepIndex].value ===
                                  ITEM_STATUS_CODES.warn ||
                                  inspectionFormPrompts[activeStepIndex].value ===
                                  ITEM_STATUS_CODES.inop) && // The value is "warn" or "inop" AND ...
                                  !(
                                    (inspectionFormPrompts[activeStepIndex].notes && inspectionFormPrompts[activeStepIndex].notes.length > 0) || // The user has added notes (1 char min) OR ...
                                    (media && media[activeStepIndex] &&
                                      media[activeStepIndex].length > 0) // The user has added audio, video, or an image.
                                  ))
                              }
                              loading={isLoading}
                              onClick={() => {
                                clearBlobUrl();
                                if (media && media[activeStepIndex]) {
                                  media[activeStepIndex].forEach(async (mediaItem) => {
                                    if (!inspectionFormPrompts[activeStepIndex]?.photo?.includes(mediaItem)) {
                                      inspectionFormPrompts[activeStepIndex]?.photo?.push(mediaItem)
                                    }
                                  });
                                }
                                activeStepIndex < inspectionFormPrompts.length - 1
                                  ? setActiveStepIndex(activeStepIndex + 1)
                                  : setShouldReviewForm(true);
                              }}
                            >
                              {activeStepIndex < inspectionFormPrompts.length - 1
                                ? "Next"
                                : "Review and Sign"}
                            </Button>
                          </Stack>
                        }
                      >

                        {inspectionFormPrompts.map((prompt, idx) => {
                          if (idx !== activeStepIndex) {
                            return null;
                          }
                          return (
                            <Box
                              key={prompt.label}
                              display={idx === activeStepIndex ? "block" : "none"}
                            >

                              {prompt.type === DEFAULT_FORM_INPUT_TYPES.standAloneQuestion && (
                                <Container>
                                  <Stack size="s">
                                    <FormField
                                      label={prompt.label}
                                      description={prompt.description}
                                    >
                                      {prompt.inputMode !== INPUT_MODES.TEXTAREA && (
                                        <Input
                                          autoFocus
                                          value={prompt.value}
                                          onChange={({ detail }) => {
                                            const updatePrompts = [...inspectionFormPrompts];
                                            updatePrompts[idx].value = detail.value;
                                            setInspectionFormPrompts(updatePrompts);
                                          }}
                                          inputMode={prompt.inputMode as InputMode | undefined}
                                          type={prompt.inputMode === INPUT_MODES.NUMBER ? "number" : "text"}
                                        />
                                      )}
                                      {prompt.inputMode === INPUT_MODES.TEXTAREA && (
                                        <Textarea
                                          autoFocus
                                          value={prompt.value}
                                          onChange={({ detail }) => {
                                            const updatePrompts = [...inspectionFormPrompts];
                                            updatePrompts[idx].value = detail.value;
                                            setInspectionFormPrompts(updatePrompts);
                                          }}
                                        />
                                      )}
                                    </FormField>
                                    {isReportUMCounterPromptLabel(prompt.label) && (
                                      <UMCounterLowReadingWarning
                                        enteredValue={prompt.value}
                                        unit={
                                          selectedUnit ||
                                          equipmentData.units?.find(
                                            (unit) => unit?.id === selectedUnitId?.value,
                                          )
                                        }
                                        unitOfMeasure={
                                          selectedUnit?.unitOfMeasure ||
                                          equipmentData.units?.find(
                                            (unit) => unit?.id === selectedUnitId?.value,
                                          )?.unitOfMeasure
                                        }
                                      />
                                    )}
                                  </Stack>
                                </Container>
                              )}
                              {prompt.type === DEFAULT_FORM_INPUT_TYPES.section && (
                                <>
                                  <Container
                                    header={<Header variant="h2">{prompt.title}</Header>}
                                    className={clsx({
                                      'flash-warning': prompt.hasActiveDiscrepancy && prompt.discrepancyStatus !== "ok",
                                      'warning-status': prompt.discrepancyStatus === ITEM_STATUS_CODES.warn
                                    })}
                                  >
                                    <Stack direction="vertical" size="xxs">
                                      {prompt.subType === (DEFAULT_FORM_INPUT_TYPES.fourResponse || DEFAULT_FORM_INPUT_TYPES.dropdown) &&
                                        <Box variant="h5">What you are inspecting:</Box>
                                      }
                                      <div className="flash">
                                        <p style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>{prompt.label}</p>
                                      </div>
                                      {prompt.description && (
                                        <>
                                          {prompt.subType !== DEFAULT_FORM_INPUT_TYPES.checkbox &&
                                            <Box variant="p">What you are looking for:</Box>}
                                          <div className="flash">
                                            <p>{prompt.description}</p>
                                          </div>
                                        </>
                                      )
                                      }
                                      {prompt.hasActiveDiscrepancy && (
                                        <Box
                                          color="text-status-warning"
                                          padding={{ top: 'xs', bottom: 'xs' }}
                                        >
                                          <div style={{
                                            backgroundColor: '#fafafa',
                                            padding: '10px',
                                            borderRadius: '4px',
                                            border: '1px solid #eaeded'
                                          }}>
                                            <strong>Previous Report Notes:</strong><br />
                                            {prompt.discrepancyNotes || 'No notes provided'}
                                          </div>
                                        </Box>
                                      )}

                                      {prompt.subType === DEFAULT_FORM_INPUT_TYPES.yesNo && (
                                        <Grid
                                          gridDefinition={[
                                            { colspan: { "default": 6, s: 3 } },
                                            { colspan: { "default": 6, s: 3 } },
                                            { colspan: { "default": 6, s: 3 } },
                                            ...((!prompt.hasActiveDiscrepancy || prompt.discrepancyStatus !== ITEM_STATUS_CODES.warn) ?
                                              [{ colspan: { "default": 6, s: 3 } }] : [])
                                          ]}
                                        >
                                          <MUIButton
                                            variant={!prompt.value || prompt.value === ITEM_STATUS_CODES.ok ? "contained" : "outlined"}
                                            color="success"
                                            style={{
                                              borderRadius: 20,
                                              fontSize: 40,
                                              padding: "20px 10px",
                                              width: "100%",
                                            }}
                                            onClick={() => {
                                              const updatePrompts = [...inspectionFormPrompts];
                                              updatePrompts[idx].value = ITEM_STATUS_CODES.ok;
                                              setInspectionFormPrompts(updatePrompts);
                                              activeStepIndex < inspectionFormPrompts.length - 1
                                                ? setActiveStepIndex(activeStepIndex + 1)
                                                : setShouldReviewForm(true);
                                            }}
                                          >
                                            Yes
                                          </MUIButton>
                                          <MUIButton
                                            variant={!prompt.value || prompt.value === ITEM_STATUS_CODES.warn ? "contained" : "outlined"}
                                            color="secondary"
                                            style={{
                                              borderRadius: 20,
                                              fontSize: prompt.hasActiveDiscrepancy && prompt.discrepancyStatus === ITEM_STATUS_CODES.warn ? '14px' : 40, padding: "20px 10px",
                                              width: "100%",
                                            }}
                                            onClick={() => {
                                              const updatePrompts = [...inspectionFormPrompts];
                                              updatePrompts[idx].value = ITEM_STATUS_CODES.warn;
                                              setInspectionFormPrompts(updatePrompts);
                                            }}
                                          >
                                            {prompt.hasActiveDiscrepancy && prompt.discrepancyStatus === ITEM_STATUS_CODES.warn
                                              ? `Status Unchanged`
                                              : `No`}

                                          </MUIButton>
                                        </Grid>
                                      )}

                                      {prompt.subType === DEFAULT_FORM_INPUT_TYPES.checkbox && (
                                        <Stack direction="vertical" size="xs">
                                          {prompt.options?.map((option: string, optionIdx: number) => (
                                            <Checkbox
                                              key={optionIdx}
                                              checked={prompt.selectedOptions?.includes(option) || false}
                                              onChange={({ detail }) => {
                                                const updatePrompts = [...inspectionFormPrompts];
                                                const currentSelected = updatePrompts[idx].selectedOptions || [];

                                                if (detail.checked) {
                                                  updatePrompts[idx].selectedOptions = [...currentSelected, option];
                                                } else {
                                                  updatePrompts[idx].selectedOptions = currentSelected.filter((val: string) => val !== option);
                                                }
                                                setInspectionFormPrompts(updatePrompts);
                                              }}
                                            >
                                              {option}
                                            </Checkbox>
                                          ))}
                                        </Stack>
                                      )}

                                      {prompt.subType === DEFAULT_FORM_INPUT_TYPES.sectionQuestion && (
                                        <>
                                          {prompt.inputMode !== INPUT_MODES.TEXTAREA && (
                                            <Input
                                              autoFocus
                                              value={prompt.value}
                                              onChange={({ detail }) => {
                                                const updatePrompts = [...inspectionFormPrompts];
                                                updatePrompts[idx].value = detail.value;
                                                setInspectionFormPrompts(updatePrompts);
                                              }}
                                              inputMode={prompt.inputMode as InputMode | undefined}
                                              type={prompt.inputMode === INPUT_MODES.NUMBER ? "number" : "text"}
                                            />
                                          )}
                                          {prompt.inputMode === INPUT_MODES.TEXTAREA && (
                                            <Textarea
                                              autoFocus
                                              value={prompt.value}
                                              onChange={({ detail }) => {
                                                const updatePrompts = [...inspectionFormPrompts];
                                                updatePrompts[idx].value = detail.value;
                                                setInspectionFormPrompts(updatePrompts);
                                              }}
                                            />
                                          )}
                                        </>
                                      )}

                                      {prompt.subType === DEFAULT_FORM_INPUT_TYPES.dropdown && (
                                        <Select
                                          selectedOption={prompt.value ? { label: prompt.value, value: prompt.value } : null}
                                          onChange={({ detail }) => {
                                            const updatePrompts = [...inspectionFormPrompts];
                                            updatePrompts[idx].value = detail.selectedOption.value ?? '';
                                            setInspectionFormPrompts(updatePrompts);
                                          }}
                                          options={prompt.options?.map((option: string) => ({
                                            label: option, // Use string as both label and value
                                            value: option
                                          })) || []}
                                          placeholder="Select an option"
                                          expandToViewport
                                        />
                                      )}
                                      {(!prompt.subType || prompt.subType === DEFAULT_FORM_INPUT_TYPES.fourResponse) && (
                                        <Grid
                                          gridDefinition={[
                                            { colspan: { "default": 6, s: 3 } },
                                            { colspan: { "default": 6, s: 3 } },
                                            { colspan: { "default": 6, s: 3 } },
                                            ...((!prompt.hasActiveDiscrepancy || prompt.discrepancyStatus !== ITEM_STATUS_CODES.warn) ?
                                              [{ colspan: { "default": 6, s: 3 } }] : [])
                                          ]}
                                        >
                                          <MUIButton
                                            variant={!prompt.value || prompt.value === ITEM_STATUS_CODES.ok ? "contained" : "outlined"}
                                            color="success"
                                            style={{
                                              borderRadius: 20,
                                              fontSize: 40,
                                              padding: "20px 10px",
                                              width: "100%",
                                            }}
                                            onClick={() => {
                                              const updatePrompts = [...inspectionFormPrompts];
                                              updatePrompts[idx].value = ITEM_STATUS_CODES.ok;
                                              setInspectionFormPrompts(updatePrompts);
                                              activeStepIndex < inspectionFormPrompts.length - 1
                                                ? setActiveStepIndex(activeStepIndex + 1)
                                                : setShouldReviewForm(true);
                                            }}
                                          >
                                            <Icon name="status-positive" size="medium" />
                                          </MUIButton>
                                          <MUIButton
                                            variant={!prompt.value || prompt.value === ITEM_STATUS_CODES.warn ? "contained" : "outlined"}
                                            color="secondary"
                                            style={{
                                              borderRadius: 20,
                                              fontSize: prompt.hasActiveDiscrepancy && prompt.discrepancyStatus === ITEM_STATUS_CODES.warn ? '14px' : 40, padding: "20px 10px",
                                              width: "100%",
                                            }}
                                            onClick={() => {
                                              const updatePrompts = [...inspectionFormPrompts];
                                              updatePrompts[idx].value = ITEM_STATUS_CODES.warn;
                                              setInspectionFormPrompts(updatePrompts);
                                            }}
                                          >
                                            {prompt.hasActiveDiscrepancy && prompt.discrepancyStatus === ITEM_STATUS_CODES.warn
                                              ? `Status Unchanged`
                                              : <Icon name="status-warning" size="medium" />
                                            }

                                          </MUIButton>
                                          <MUIButton
                                            variant={!prompt.value || prompt.value === ITEM_STATUS_CODES.inop ? "contained" : "outlined"}
                                            color="error"
                                            style={{
                                              borderRadius: 20,
                                              padding: "20px 10px",
                                              fontSize: 40,
                                              width: "100%",
                                            }}
                                            onClick={() => {
                                              const updatePrompts = [...inspectionFormPrompts];
                                              updatePrompts[idx].value = ITEM_STATUS_CODES.inop;
                                              setInspectionFormPrompts(updatePrompts);
                                            }}
                                          >
                                            <Icon name="status-negative" size="medium" />
                                          </MUIButton>
                                          {(!prompt.hasActiveDiscrepancy || prompt.discrepancyStatus !== ITEM_STATUS_CODES.warn) && (
                                            <MUIButton
                                              variant={!prompt.value || prompt.value === ITEM_STATUS_CODES.na || prompt.value === ITEM_STATUS_CODES.under_review ? "contained" : "outlined"}
                                              color={prompt.hasActiveDiscrepancy ? "primary" : "warning"}
                                              sx={{
                                                '&.MuiButton-containedWarning': {
                                                  backgroundColor: '#f57c00',
                                                  '&:hover': {
                                                    backgroundColor: '#ef6c00'
                                                  }
                                                }
                                              }}
                                              style={{
                                                borderRadius: 20,
                                                fontSize: '14px',
                                                padding: "20px 10px",
                                                width: "100%",
                                              }}
                                              onClick={() => {
                                                const updatePrompts = [...inspectionFormPrompts];
                                                if (prompt.hasActiveDiscrepancy) {
                                                  // If there's an active discrepancy (not warn), keep the existing status
                                                  updatePrompts[idx].value = prompt.discrepancyStatus || ITEM_STATUS_CODES.under_review;
                                                } else {
                                                  // If no active discrepancy, set to N/A
                                                  updatePrompts[idx].value = ITEM_STATUS_CODES.na;
                                                  activeStepIndex < inspectionFormPrompts.length - 1
                                                    ? setActiveStepIndex(activeStepIndex + 1)
                                                    : setShouldReviewForm(true);
                                                }
                                                setInspectionFormPrompts(updatePrompts);
                                              }}
                                            >
                                              {prompt.hasActiveDiscrepancy ? "Status Unchanged" : 'N/A'}
                                            </MUIButton>
                                          )}
                                        </Grid>
                                      )}

                                      {inspectionFormPrompts[idx].value &&
                                        inspectionFormPrompts[idx].value !== ITEM_STATUS_CODES.ok &&
                                        (prompt.subType === DEFAULT_FORM_INPUT_TYPES.fourResponse ||
                                          prompt.subType === DEFAULT_FORM_INPUT_TYPES.yesNo || prompt.subType === DEFAULT_FORM_INPUT_TYPES.checkbox ||
                                          !prompt.subType) && (
                                          <>
                                            <FormField label="Notes">
                                              <Textarea
                                                value={prompt.notes || (prompt.hasActiveDiscrepancy ? prompt.discrepancyNotes ?? '' : '')}
                                                onChange={({ detail }) => {
                                                  const updatePrompts = [...inspectionFormPrompts];
                                                  updatePrompts[idx].notes = detail.value;
                                                  setInspectionFormPrompts(updatePrompts);
                                                }}
                                              />
                                            </FormField>
                                            <Container header={<h2>Add Audio Note</h2>}>
                                              <Stack direction="horizontal" size="xxl">
                                                {/* Audio recording functionality remains unchanged */}
                                                {isAudioRecordingStopped(status) && (
                                                  <Button
                                                    onClick={startRecording}
                                                    iconAlign="left"
                                                    iconName="microphone"
                                                  >
                                                    Record
                                                  </Button>
                                                )}
                                                {!isAudioRecordingStopped(status) && (
                                                  <Button
                                                    onClick={stopRecording}
                                                    loading={status === "acquiring_media"}
                                                    iconAlign="left"
                                                    iconName="microphone-off"
                                                  >
                                                    {status === "recording" && <>End Recording</>}
                                                    {status === "acquiring_media" && <>Starting</>}
                                                  </Button>
                                                )}
                                                {audioReview[activeStepIndex] && (
                                                  <>
                                                    <audio
                                                      src={URL.createObjectURL(audioReview[activeStepIndex])}
                                                      controls
                                                    >
                                                      <source
                                                        src={URL.createObjectURL(audioReview[activeStepIndex])}
                                                        type="audio/wav"
                                                      />
                                                    </audio>
                                                    <Button
                                                      iconAlign="left"
                                                      iconName="close"
                                                      onClick={() => {
                                                        const newMedia = { ...media };
                                                        delete newMedia[activeStepIndex];
                                                        setMedia(newMedia);
                                                        const newAudioReview = { ...audioReview };
                                                        delete newAudioReview[activeStepIndex];
                                                        setAudioReview(newAudioReview);
                                                      }}
                                                    >
                                                      Remove
                                                    </Button>
                                                  </>
                                                )}
                                              </Stack>
                                            </Container>
                                            <Dropzone
                                              addClassNames={'video-dropzone' as IStyleCustomization<string>}
                                              onChangeStatus={handleChangeStatus}
                                              accept="image/jpeg, image/jpg, image/png, audio/wav, audio/mp3, video/*"
                                              inputContent={(_files, extra) => {
                                                if (extra.reject) {
                                                  return "Image or video files only";
                                                }
                                                return "Click to add images or videos";
                                              }}
                                            />
                                          </>
                                        )}
                                      <Box>
                                        <Stack direction="horizontal" size="l">
                                          {prompt?.photo?.map((photo, index) => {
                                            return (
                                              <div
                                                key={index}
                                                className="photo-container"
                                              >
                                                <img
                                                  key={index}
                                                  src={URL.createObjectURL(photo?.data?.file as unknown as MediaSource)}
                                                  alt={prompt.label}
                                                  className="photo"
                                                />
                                                <div className="delete-image">
                                                  <Button
                                                    variant="icon"
                                                    iconName="close"
                                                    onClick={async () => {
                                                      await deleteRecord(photo?.id as Key)
                                                      const updatedFormPrompts = [
                                                        ...inspectionFormPrompts,
                                                      ];
                                                      updatedFormPrompts[
                                                        idx
                                                      ].photo?.splice(index, 1);
                                                      if (media) {
                                                        setMedia((prev) => {
                                                          return {
                                                            ...prev,
                                                            [activeStepIndex]: media[activeStepIndex].filter(
                                                              (el) => el.id !== photo?.id
                                                            ),
                                                          };
                                                        });
                                                      }
                                                      setInspectionFormPrompts(
                                                        updatedFormPrompts
                                                      );
                                                    }}
                                                  />
                                                </div>
                                              </div>
                                            )
                                          })}
                                        </Stack>
                                      </Box>
                                    </Stack>
                                  </Container>
                                </>
                              )}

                            </Box>
                          );
                        })}
                      </Form>
                    </form>
                  </Stack>
                </Container>
              </>
            )}
          </Stack>
        </ThemeProvider>
      }
    />
  );
};
